import React, { useCallback, useEffect, useState } from "react";

import { supabase } from "../supabase";
import {
  getClientWorkspaceLaunchLabel,
  isWorkspaceProvisioned,
  loadClientWorkspaceSummary,
  openLinkedClientWorkspace,
  provisionClientWorkspace,
} from "../helpers/clientWorkspace";
import "./ClientWorkspaceSection.css";

/** Temporary allowlist — Nexora operations org only. Replace with proper auth later. */
const TEMP_ALLOWED_ORGANIZATION_ID = "c3a10001-0001-4001-8001-000000000001";
/** Fallback when organization_id is missing from client actor state. */
const TEMP_ALLOWED_SUPABASE_USER_ID = "ce969799-d147-473d-a4bd-f49b01c5711c";

function actorMatchesTemporaryAllowlist(actor) {
  if (!actor) {
    return false;
  }

  if (actor.organization_id === TEMP_ALLOWED_ORGANIZATION_ID) {
    return true;
  }

  const userId =
    actor.supabase_user_id || actor.user_id || actor.id || null;
  return userId === TEMP_ALLOWED_SUPABASE_USER_ID;
}

/**
 * Edit Client — Client Workspace launcher.
 * Temporary hard-coded allowlist (Nexora ops org / Joey user id). No RPC for visibility.
 * Open/create reuses the CrewPilot Supabase session via /auth/crewpilot handoff.
 *
 * - Provisioned space for this client → "Open Client Workspace"
 * - No space yet → "Create Client Workspace" (space only; no Website Projects)
 */
export function ClientWorkspaceSection({
  clientId,
  clientName,
  actor,
}) {
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(Boolean(clientId));
  const [space, setSpace] = useState(null);
  const [error, setError] = useState(null);

  const refreshSummary = useCallback(async ({ clearError = true } = {}) => {
    if (!clientId) {
      setSpace(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const summary = await loadClientWorkspaceSummary(supabase, clientId);
      setSpace(summary.space || null);
      if (clearError) {
        setError(null);
      }
    } catch (err) {
      console.error("[ClientWorkspace] space lookup failed:", err);
      setSpace(null);
      setError("Unable to load client workspace status.");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void refreshSummary();
  }, [refreshSummary]);

  if (!actorMatchesTemporaryAllowlist(actor)) {
    return null;
  }

  const provisioned = isWorkspaceProvisioned(space);
  const label = getClientWorkspaceLaunchLabel(space);

  async function handleOpenExisting() {
    if (!space?.slug) {
      setError("This client does not have a Client Workspace yet.");
      return;
    }

    const opened = await openLinkedClientWorkspace(supabase, space);
    if (!opened) {
      setError("Unable to open client workspace.");
    }
  }

  async function handleCreate() {
    if (!clientId) {
      setError("Save the client before creating a Client Workspace.");
      return;
    }

    const created = await provisionClientWorkspace(supabase, actor, {
      clientId,
      displayName: clientName,
    });
    setSpace(created);

    const opened = await openLinkedClientWorkspace(supabase, created);
    if (!opened) {
      setError(
        "Client Workspace was created, but it could not be opened automatically.",
      );
    }
  }

  async function handleLaunch() {
    if (busy || loading) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (provisioned) {
        await handleOpenExisting();
      } else {
        await handleCreate();
      }
    } catch (err) {
      console.error("[ClientWorkspace] launch failed:", err);
      await refreshSummary({ clearError: false });
      setError(
        err?.message ||
          (provisioned
            ? "Unable to open client workspace."
            : "Unable to create client workspace."),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="cp-workspace-section" aria-labelledby="cp-workspace-heading">
      <h3 id="cp-workspace-heading" className="cp-section-title kpi-section-label">
        CLIENT WORKSPACE
      </h3>

      <button
        type="button"
        className="nx-btn nx-btn-gold cp-workspace-primary"
        disabled={busy || loading || !clientId}
        onClick={handleLaunch}
      >
        {busy
          ? provisioned
            ? "Opening…"
            : "Creating…"
          : loading
            ? "Loading…"
            : label}
      </button>

      {error ? (
        <p className="cp-workspace-error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
